import { useEffect, useState } from "react";
import { Card } from "../../shared";
import { AreaChart } from "../../shared/Charts";
import { dateStage } from "../../constants/aggregate";
import { getParentCompany } from "../../utils/authority";
import { transformReportDataForCharts } from "../../utils/chartDataTransform";
import {
  endOfWeek,
  endOfMonth,
  startOfMonth,
  startOfDay,
  endOfToday,
  startOfWeek,
  startOfYear,
  endOfYear,
} from "date-fns";
import request from "../../utils/request";
import { buildOperationalUtcWindow } from "../../utils/date.utils";
import { getProductTerminology } from "../../constants/product-terminology";

export const JobStatusReport = (props) => {
  const [reportData, setReportData] = useState();
  const [resolvedRange, setResolvedRange] = useState(null);
  const terms = getProductTerminology(props.appType);

  useEffect(() => {
    fetchReport({
      type: props.viewType,
      startDate: props.startDate,
      endDate: props.endDate,
    });
  }, [props.viewType, props.startDate, props.endDate]);

  const newDate = new Date();

  // Start of day
  const startOfDayDate = startOfDay(newDate);
  const endOfDayDate = endOfToday(newDate);

  // Start of week
  const startOfWeekDate = new Date(startOfWeek(newDate));
  const endOfWeekDate = new Date(endOfWeek(newDate));

  // Start of month
  const startOfMonthDate = new Date(startOfMonth(newDate));
  const endOfMonthDate = new Date(endOfMonth(newDate));

  // Start/End of year
  const startOfYearDate = new Date(startOfYear(newDate));
  const endOfYearDate = new Date(endOfYear(newDate));

  // Very early date for "all" option (January 1, 2000)
  const veryEarlyDate = new Date(2000, 0, 1);
  // Far future date for "all" option (10 years from now)
  const farFutureDate = new Date(newDate.getFullYear() + 10, 11, 31);

  const fetchReport = async (view) => {
    const type = view.type;
    let start, end;

    // Check if custom dates are provided
    if (view.startDate && view.endDate) {
      start = new Date(view.startDate);
      end = new Date(view.endDate);
    } else {
      // Use predefined ranges based on type
      if (type === "daily") {
        start = startOfDayDate;
        end = endOfDayDate;
      } else if (type === "weekly") {
        start = startOfWeekDate;
        end = endOfWeekDate;
      } else if (type === "monthly") {
        start = startOfMonthDate;
        end = endOfMonthDate;
      } else if (type === "yearly") {
        start = startOfYearDate;
        end = endOfYearDate;
      } else if (type === "all") {
        start = veryEarlyDate;
        end = farFutureDate;
      } else {
        // Default to weekly if type is not recognized
        start = startOfWeekDate;
        end = endOfWeekDate;
      }
    }

    const normalizedRange = buildOperationalUtcWindow(start, end);

    const stages = dateStage(
      props.appType === "serviceRepair" ? "appointmentDate" : "transactionDate",
      normalizedRange.startDate || start.toISOString(),
      normalizedRange.endDate || end.toISOString(),
      type,
      getParentCompany(),
      props.appType,
    );
    setResolvedRange({ start, end });

    const response = await request(`/reports`, {
      method: "post",
      data: {
        stages,
        modelName: "Job",
        config: {
          0: {
            type: "$match",
            useDate:
              props.appType === "serviceRepair"
                ? "appointmentDate"
                : "transactionDate",
          },
        },
      },
    });

    return Promise.all([response]).then((values) => {
      const responseData = values[0];

      // Try to get the actual data array
      let finalData;
      if (responseData?.data?.data && Array.isArray(responseData.data.data)) {
        finalData = responseData.data.data;
      } else if (responseData?.data && Array.isArray(responseData.data)) {
        finalData = responseData.data;
      } else {
        console.error("Could not find data array in response");
        finalData = [];
      }

      // Transform new data format to old chart format if needed
      const transformedData = transformReportDataForCharts(finalData);
      setReportData(transformedData);
    });
  };

  return (
    // col-span-3 md:col-span-6
    <div className="min-h-[430px] rounded-lg row-span-1 flex flex-col font-bold h-full">
      <Card className="h-full">
        <div className="mb-3 ext-xs md:text-sm uppercase text-gray-500 ">
          {terms.job} Statuses
        </div>
        <AreaChart
          height={350}
          data={reportData}
          appType={props.appType}
          dataType="jobs"
          viewType={props.viewType}
          rangeStart={resolvedRange?.start}
          rangeEnd={resolvedRange?.end}
        />
      </Card>
    </div>
  );
};
